import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { PlusIcon } from './CustomSvgIcons';

export default function DisplayNestedTable(props) {
  const {
    entity,
    tableTitles,
    tableEntries,
    nestedConfig,
    currDarkThemeClass,
    enablePagination = false,
    toShowAddBtn = false,
    emptyTableMsg,
    data, // New prop for recursive data
    renderRow, // New prop for rendering a row from data
    getNestedData, // New prop to extract nested data
    nestedRenderRow, // New prop for rendering nested rows (for recursion)
    nestedTableTitles, // New prop for nested table titles
    parent, // New prop: context for the current list (the parent item)
  } = props;
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const isDarkTheme = theme.palette.themeMode === 'dark' || currDarkThemeClass?.includes('dark');

  const themeColors = {
    background: isDarkTheme ? theme.palette.dark[800] : theme.palette.background.default,
    cardBackground: isDarkTheme ? theme.palette.dark[700] : theme.palette.primary.dashboard.white,
    text: isDarkTheme ? theme.palette.grey[200] : theme.palette.primary.dashboard.cardValue,
    border: isDarkTheme ? 'rgba(255, 255, 255, 0.3)' : theme.palette.primary.dashboard.divider,
    headerBackground: isDarkTheme ? theme.palette.dark[800] : theme.palette.primary[100],
    hoverBackground: isDarkTheme ? theme.palette.dark[600] : theme.palette.primary[100],
    nestedBackground: isDarkTheme ? theme.palette.dark[900] : theme.palette.primary.dashboard.bg,
    connectingLine: isDarkTheme ? theme.palette.grey[600] : theme.palette.grey[400],
    buttonText: theme.palette.primary.dashboard.white,
    buttonBg: theme.palette.primary.dashboard.cardBtn,
  };

  const toggleRow = index => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: themeColors.cardBackground,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
  };

  const headerStyle = {
    backgroundColor: themeColors.headerBackground,
    color: themeColors.text,
    fontWeight: '600',
    fontSize: '14px',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: `1px solid ${themeColors.border}`,
    borderRight: `1px solid ${themeColors.border}`,
  };

  const cellStyle = {
    padding: '12px 16px',
    borderBottom: `1px solid ${themeColors.border}`,
    borderRight: `1px solid ${themeColors.border}`,
    fontSize: '14px',
    verticalAlign: 'middle',
  };

  // Used for existing backward compatibility
  const nestedTableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: themeColors.cardBackground,
    marginTop: '8px',
    border: `1px solid ${themeColors.border}`,
    borderRadius: '6px',
    overflow: 'hidden',
  };

  const nestedHeaderStyle = {
    backgroundColor: themeColors.headerBackground,
    color: themeColors.text,
    fontWeight: '500',
    fontSize: '13px',
    padding: '10px 12px',
    textAlign: 'left',
    borderBottom: `1px solid ${themeColors.border}`,
    borderRight: `1px solid ${themeColors.border}`,
  };

  const nestedCellStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${themeColors.border}`,
    borderRight: `1px solid ${themeColors.border}`,
    fontSize: '13px',
    verticalAlign: 'middle',
  };

  const expandButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: themeColors.text,
  };

  const verticalLineStyle = {
    position: 'absolute',
    left: '24px',
    top: '0',
    bottom: '0',
    width: '1px',
    backgroundColor: themeColors.connectingLine,
    content: '""',
  };

  // Helper to determine if we should show expand button
  const hasNested = rowIndex => {
    if (data) {
      const item = data[rowIndex];
      const nested = getNestedData ? getNestedData(item) : [];
      return nested && nested.length > 0;
    }
    return nestedConfig?.hasNestedData(rowIndex);
  };

  return (
    <div style={{ padding: '16px', backgroundColor: themeColors.background }}>
      {toShowAddBtn && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: themeColors.buttonBg,
              color: themeColors.buttonText,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <PlusIcon size={16} />
            Add {entity}
          </button>
        </div>
      )}

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          borderRadius: '8px',
          border: `1px solid ${themeColors.border}`,
          '&::-webkit-scrollbar': {
            height: '6px', // Thin scrollbar
          },
          '&::-webkit-scrollbar-track': {
            background: isDarkTheme ? '#2a2a2a' : '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isDarkTheme ? '#555' : '#888',
            borderRadius: '4px',
            '&:hover': {
              background: isDarkTheme ? '#777' : '#555',
            },
          },
        }}
      >
        <table style={{ ...tableStyle, border: 'none' }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: '40px', whiteSpace: 'nowrap' }}></th>
              {tableTitles.map((title, index) => (
                <th key={index} style={{ ...headerStyle, whiteSpace: 'nowrap' }}>
                  {title.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data ? data.length === 0 : tableEntries.length === 0) ? (
              <tr>
                <td
                  colSpan={tableTitles.length + 1}
                  style={{
                    ...cellStyle,
                    textAlign: 'center',
                    color: themeColors.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {emptyTableMsg ?? `No ${entity} Added`}
                </td>
              </tr>
            ) : (
              (data || tableEntries).map((entry, rowIndex) => {
                // Determine cells based on mode
                const cells = data
                  ? renderRow(entry, rowIndex, parent) // entry is data item, parent is context
                  : entry; // entry is cells array

                return (
                  <React.Fragment key={rowIndex}>
                    <tr style={{ backgroundColor: expandedRows.has(rowIndex) ? themeColors.hoverBackground : 'transparent' }}>
                      <td style={{ ...cellStyle, width: '40px', position: 'relative', whiteSpace: 'nowrap' }}>
                        {hasNested(rowIndex) && (
                          <button
                            onClick={() => toggleRow(rowIndex)}
                            style={expandButtonStyle}
                            aria-expanded={expandedRows.has(rowIndex)}
                            aria-controls={`nested-row-${rowIndex}`}
                          >
                            {expandedRows.has(rowIndex) ? <ExpandMoreIcon size={16} /> : <ChevronRightIcon size={16} />}
                          </button>
                        )}
                      </td>
                      {cells.map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                          {cell}
                        </td>
                      ))}
                    </tr>

                    {hasNested(rowIndex) && (
                      <tr id={`nested-row-${rowIndex}`}>
                        <td colSpan={tableTitles.length + 1} style={{ padding: '0', borderBottom: 'none' }}>
                          <Collapse in={expandedRows.has(rowIndex)} timeout="auto" unmountOnExit>
                            <div
                              style={{
                                padding: '16px 24px',
                                backgroundColor: themeColors.nestedBackground,
                                position: 'relative',
                                // Add clearer borders for nested differentiation
                                borderTop: `1px solid ${themeColors.border}`,
                                borderBottom: `1px solid ${themeColors.border}`,
                                boxShadow: 'inset 0px 4px 8px -4px rgba(0,0,0,0.1)', // Inner shadow for depth
                                overflowX: 'auto', // Ensure nested content also scrolls if needed
                              }}
                            >
                              <div style={verticalLineStyle}></div>

                              {data ? (
                                // Recursive Mode
                                <DisplayNestedTable
                                  entity={entity}
                                  tableTitles={nestedTableTitles || tableTitles}
                                  data={getNestedData(entry)}
                                  renderRow={nestedRenderRow || renderRow}
                                  getNestedData={getNestedData}
                                  nestedRenderRow={nestedRenderRow}
                                  nestedTableTitles={nestedTableTitles}
                                  currDarkThemeClass={currDarkThemeClass}
                                  toShowAddBtn={false} // Disable add button and pagination for nested
                                  enablePagination={false}
                                  parent={entry}
                                />
                              ) : (
                                // Legacy Mode (Flat Nested Table)
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={nestedTableStyle}>
                                    <thead>
                                      <tr>
                                        {nestedConfig.nestedTableTitles.map((title, index) => (
                                          <th key={index} style={{ ...nestedHeaderStyle, whiteSpace: 'nowrap' }}>
                                            {title.title}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {nestedConfig.processNestedData(rowIndex).map((nestedEntry, nestedIndex) => (
                                        <tr key={nestedIndex}>
                                          {nestedEntry.map((cell, cellIndex) => (
                                            <td key={cellIndex} style={{ ...nestedCellStyle, whiteSpace: 'nowrap' }}>
                                              {cell}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      {enablePagination && (
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'end',
            paddingRight: '20px',
            color: themeColors.text,
          }}
        >
          {/* Pagination controls would go here */}
        </div>
      )}
    </div>
  );
}
